import type { DisplayOrderTree, IOrderRollback, DisplayOrderTreeZone } from '../domain';
import { DisplayOrder } from './DisplayOrder';
import { DisplayOrderChangeset } from './DisplayOrderChangeset';
import { DisplayOrderTreeMap } from './DisplayOrderTreeMap';
import type { IDisplayOrderKeyGenerator } from './IDisplayOrderKeyGenerator';
import type { ModelId, NodeId, StickyZoneId, ViewModelId } from 'balus-common';
import type { Reference } from '@framework/repository';
import { RefBuilder } from '@framework/repository';
import { RTDBPath } from 'balus-common';

/**
 * 新しいキーを生成する
 * @param ref
 */
const newKey = (ref: Reference): string => {
  // https://firebase.google.com/docs/database/web/lists-of-data?hl=ja#append_to_a_list_of_data
  // push() 参照の .key プロパティには自動生成されたキーの値が含まれているため string とみなす。
  return ref.push().key as string;
};

class OrderKeyGenerator implements IDisplayOrderKeyGenerator {
  constructor(private readonly ref: Reference) {}

  generate(): string {
    return newKey(this.ref);
  }
}

export class DisplayOrderRepository {
  private readonly ref: Reference;

  constructor(viewModelId: ViewModelId, modelId: ModelId) {
    this.ref = RefBuilder.ref(RTDBPath.Model.displayOrderPath(viewModelId, modelId));
  }

  private async fetchTree(): Promise<DisplayOrderTreeMap> {
    const snapshot = await this.ref.once('value');
    return new DisplayOrderTreeMap(snapshot.val() || {});
  }

  /**
   * 並び順の情報を一括取得する
   */
  async load(): Promise<DisplayOrderTree> {
    return (await this.fetchTree()).toDisplayOrderTree('__root__');
  }

  /**
   * 並び順の情報を一括保存する
   *
   * @param tree
   */
  async save(tree: DisplayOrderTree, parentIds?: StickyZoneId[]): Promise<void> {
    const { nodeIds, zones } = tree;
    const zoneIds = zones.map((zone: DisplayOrderTreeZone) => zone.id);
    if (parentIds && parentIds.length > 0) {
      await Promise.all(
        parentIds.flatMap((parentId) => [this.addNodes(nodeIds, parentId), this.addZones(zoneIds, parentId)])
      );
    } else {
      await this.addNodes(nodeIds, undefined);
      await this.addZones(zoneIds, undefined);
    }

    await Promise.all(zones.map((zone: DisplayOrderTreeZone) => this.saveZone(zone)));
  }

  private async saveZone(treeZone: DisplayOrderTreeZone): Promise<void> {
    const { id, nodeIds, zones } = treeZone;
    const zoneIds = zones.map((zone: DisplayOrderTreeZone) => zone.id);
    await this.addNodes(nodeIds, id);
    await this.addZones(zoneIds, id);
    await Promise.all(zones.map((zone: DisplayOrderTreeZone) => this.saveZone(zone)));
  }

  /**
   * ノードを指定した親内の最前面に追加する。親ID（ゾーンID）を指定しなかった場合はルート（ゾーンなし）へ追加する。
   *
   * @param nodeId ノードID
   * @param parentId 親ID（ゾーンID）
   */
  async addNode(nodeId: NodeId, parentId?: StickyZoneId): Promise<void> {
    return this.addNodes([nodeId], parentId);
  }

  /**
   * ノードを指定した親内の最前面に追加する。親ID（ゾーンID）を指定しなかった場合はルート（ゾーンなし）へ追加する。
   *
   * @param nodeIds ノードID
   * @param parentId 親ID（ゾーンID）
   */
  async addNodes(nodeIds: NodeId[], parentId?: StickyZoneId): Promise<void> {
    const { ref } = this;

    const orders = nodeIds.map((id) => {
      // Realtime Database に時系列順のキーを生成してもらう
      return DisplayOrder.node(id, parentId || '__root__', newKey(ref));
    });

    await DisplayOrderChangeset.changeset().addOrders(orders).saveTo(ref);
  }

  /**
   * 並び順からノードを削除する。Undo時などの復元用インタフェースを返す。
   * @param id ノードID
   * @return 復元用のインタフェース。rollback() で利用する。
   */
  async removeNode(id: NodeId): Promise<IOrderRollback> {
    return this.removeNodes([id]);
  }

  /**
   * 並び順からノードを削除する。Undo時などの復元用インタフェースを返す。
   * @param ids ノードID
   * @return 復元用のインタフェース。rollback() で利用する。
   */
  async removeNodes(ids: NodeId[]): Promise<IOrderRollback> {
    return this.removeNodesAndZones(ids, []);
  }

  /**
   * ゾーンを指定した親内の最前面に追加する。親ID（ゾーンID）を指定しなかった場合はルート（ゾーンなし）へ追加する。
   *
   * @param zoneId ゾーンID
   * @param parentId 親ID（ゾーンID）
   */
  async addZone(zoneId: StickyZoneId, parentId?: StickyZoneId): Promise<void> {
    return this.addZones([zoneId], parentId);
  }

  /**
   * ゾーンを指定した親内の最前面に追加する。親ID（ゾーンID）を指定しなかった場合はルート（ゾーンなし）へ追加する。
   *
   * @param zoneIds ゾーンID
   * @param parentId 親ID（ゾーンID）
   */
  async addZones(zoneIds: StickyZoneId[], parentId?: StickyZoneId): Promise<void> {
    const { ref } = this;

    const orders = zoneIds.map((id) => {
      // Realtime Database に時系列順のキーを生成してもらう
      return DisplayOrder.zone(id, parentId || '__root__', newKey(ref));
    });

    await DisplayOrderChangeset.changeset().addOrders(orders).saveTo(ref);
  }

  /**
   * 並び順からゾーンを削除する。Undo時などの復元用インタフェースを返す。
   * @param zoneId ゾーンID
   * @return 復元用のインタフェース。rollback() で利用する。
   */
  async removeZone(id: StickyZoneId): Promise<IOrderRollback> {
    return this.removeZones([id]);
  }

  /**
   * 並び順からゾーンを削除する。Undo時などの復元用インタフェースを返す。
   * @param zoneIds ゾーンID
   * @return 復元用のインタフェース。rollback() で利用する。
   */
  async removeZones(zoneIds: StickyZoneId[]): Promise<IOrderRollback> {
    return this.removeNodesAndZones([], zoneIds);
  }

  /**
   * 並び順からノードとゾーンを削除する。Undo時などの復元用インタフェースを返す。
   * @param nodeIds ノードID
   * @param zoneIds ゾーンID
   * @return 復元用のインタフェース。rollback() で利用する。
   */
  async removeNodesAndZones(nodeIds: NodeId[], zoneIds: StickyZoneId[]): Promise<IOrderRollback> {
    const { ref } = this;

    return (await this.fetchTree())
      .removeNodesAndZonesChangeset(new OrderKeyGenerator(ref), nodeIds, zoneIds)
      .saveTo(ref);
  }

  /**
   * ノード・ゾーンの追加と削除を1回のRTDB更新にまとめて実行する。
   * @param removedNodeIds 削除するノードID
   * @param removedZoneIds 削除するゾーンID
   * @param addedNodeIds 追加するノードID
   * @param addedZoneIds 追加するゾーンID
   */
  async patchNodesAndZones(
    removedNodeIds: NodeId[],
    removedZoneIds: StickyZoneId[],
    addedNodeIds: NodeId[],
    addedZoneIds: StickyZoneId[]
  ): Promise<void> {
    const { ref } = this;
    const hasRemove = removedNodeIds.length > 0 || removedZoneIds.length > 0;

    // remove がある場合はツリーを取得して削除 changeset を作成、なければ空の changeset から開始
    let changeset = hasRemove
      ? (await this.fetchTree()).removeNodesAndZonesChangeset(
          new OrderKeyGenerator(ref),
          removedNodeIds,
          removedZoneIds
        )
      : DisplayOrderChangeset.changeset();

    // 追加ノード
    if (addedNodeIds.length > 0) {
      const nodeOrders = addedNodeIds.map((id) => DisplayOrder.node(id, '__root__', newKey(ref)));
      changeset = changeset.addOrders(nodeOrders);
    }

    // 追加ゾーン
    if (addedZoneIds.length > 0) {
      const zoneOrders = addedZoneIds.map((id) => DisplayOrder.zone(id, '__root__', newKey(ref)));
      changeset = changeset.addOrders(zoneOrders);
    }

    await changeset.saveTo(ref);
  }

  /**
   * 並び順ツリー全体を置き換える。
   *
   * 旧ツリーの全要素の削除と新ツリーの全要素の追加を1つのchangesetにまとめ、
   * 単一のRTDB更新で適用する。中間状態(空・部分的なツリー)が他クライアントから
   * 見えないように、削除→保存を分けずにアトミックに置換する。
   */
  async replaceTree(oldTree: DisplayOrderTree, newTree: DisplayOrderTree): Promise<void> {
    const { ref } = this;
    const treeMap = await this.fetchTree();

    // ゾーンエントリーの親パス削除({zoneId: null})は、同一update内での
    // 子パス(zoneId/nodeOrders/*等)の再追加とRTDBのmulti-location updateで
    // 衝突するため、既存orderのパス(parentId/type/id)単位で削除する
    const removeOrders = [
      ...treeMap.findNodeOrders(oldTree.allNodeIds()),
      ...treeMap.findZoneOrders(oldTree.allZoneIds()),
    ];

    await DisplayOrderChangeset.changeset()
      .removeOrders(removeOrders)
      .addOrders(this.buildTreeOrders(newTree))
      .saveTo(ref);
  }

  /**
   * ツリー全体のDisplayOrderリストを親子関係付きで構築する。
   */
  private buildTreeOrders(tree: DisplayOrderTree): DisplayOrder[] {
    const { ref } = this;
    const orders: DisplayOrder[] = [
      ...tree.nodeIds.map((id) => DisplayOrder.node(id, '__root__', newKey(ref))),
      ...tree.zones.map((zone) => DisplayOrder.zone(zone.id, '__root__', newKey(ref))),
    ];
    tree.zones.forEach((zone) => this.collectZoneOrders(zone, orders));
    return orders;
  }

  private collectZoneOrders(treeZone: DisplayOrderTreeZone, orders: DisplayOrder[]): void {
    const { ref } = this;
    treeZone.nodeIds.forEach((id) => orders.push(DisplayOrder.node(id, treeZone.id, newKey(ref))));
    treeZone.zones.forEach((child) => {
      orders.push(DisplayOrder.zone(child.id, treeZone.id, newKey(ref)));
      this.collectZoneOrders(child, orders);
    });
  }

  /**
   * 所属するゾーンを変更する。
   * @param childIds 親を変更するノードまたはゾーンのIDリスト
   * @param newParentZoneId 変更先の親ゾーン。nullを指定した場合はルート直下（親ゾーンなし）に変更します。
   */
  async changeParentZones(
    childIds: (NodeId | StickyZoneId)[],
    newParentZoneIds: StickyZoneId[]
  ): Promise<IOrderRollback> {
    const { ref } = this;

    const parentZoneIds = newParentZoneIds.length ? newParentZoneIds : ['__root__'];

    const tree = await this.fetchTree();

    // オーダーキーを新たに発行するのでキー順が同じ順序になるようオーダキー順で並べる
    const oldOrders = DisplayOrder.sortByOrderKey(
      tree.findOrders(childIds).filter((order) => {
        // 複数の親に追加する場合は、全ての要素が処理対象
        // （orderは1つの親しか持てないため、必ず少なくとも1つの新しい親が必要）
        if (parentZoneIds.length > 1) return true;

        // 単一の親に移動する場合、既にその親である要素は除外
        return !order.isChildOfAny(parentZoneIds);
      })
    );
    const newOrders = oldOrders.flatMap((order: DisplayOrder) =>
      parentZoneIds.map((parentZoneId) => order.changeParent(parentZoneId, newKey(ref)))
    );

    return DisplayOrderChangeset.changeset().removeOrders(oldOrders).addOrders(newOrders).saveTo(ref);
  }

  /**
   * changeParentZone() 実行時に親変更が起きるかどうかを返します。
   *
   * @param childIds
   * @param newParentZoneId
   */
  async willChangeParents(childIds: (NodeId | StickyZoneId)[], newParentZoneIds: StickyZoneId[]): Promise<boolean> {
    const parentZoneIds = newParentZoneIds.length > 0 ? newParentZoneIds : ['__root__'];
    const displayOrders = (await this.fetchTree()).findOrders(childIds);

    // 複数の親への追加の場合、要素が1つでもあれば必ず変更が発生
    if (parentZoneIds.length > 1) {
      return displayOrders.length > 0;
    }

    // 単一の親への移動の場合、その親でない要素が1つでもあれば変更が発生
    return displayOrders.some((order) => !order.isChildOfAny(parentZoneIds));
  }

  /**
   * 並び順の変更を復元する
   *
   * @param orderRollback 削除などの操作メソッドから返される復元用のインタフェース
   */
  async rollback(orderRollback: IOrderRollback): Promise<void> {
    await orderRollback.rollback(this.ref);
  }
}
